import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { UserService } from '@app/providers/user/user.service';
import { BehaviorSubject, combineLatest, Observable, debounceTime, distinctUntilChanged, map, switchMap, take } from 'rxjs';

import { API_ROUTE_PRODUCTS } from '../api/api-router';
import { ProductResponse } from '../definition/interface/product';
import { SubscriptionFacadeService } from './subscription-facade.service';

export interface CriteriaSort {
  direction: 'ASC' | 'DESC';
  property: string;
}
export interface ProductCriteria {
  autocomplete?: string;
  enabled?: boolean;
  status?: string;
  categories?: string[];
  type?: string[];
  ids?: string[];
  organizationIds?: number[];
  page?: number;
  size?: number;
  distinct?: boolean;
  sorts?: CriteriaSort[];
  onlineSubscription?: boolean;
}

export interface ProductState {
  products: ProductResponse;

  criteria: ProductCriteria;
  loading: boolean;
}
let _state: ProductState = {
  products: null,
  criteria: {
    sorts: [
      {
        direction: 'ASC',
        property: 'NAME',
      },
      {
        direction: 'ASC',
        property: 'MAPPING_DATE',
      },
    ],
    enabled: true,
    status: 'VALID',
    page: 1,
    size: 5,

    type: ['SCPI'],
    onlineSubscription: true,
  },

  loading: false,
};

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private store = new BehaviorSubject<ProductState>(_state);
  private state$ = this.store.asObservable();

  products$ = this.state$.pipe(
    map(state => state.products),
    distinctUntilChanged(),
  );
  criteria$ = this.state$.pipe(
    map(state => state.criteria),
    distinctUntilChanged(),
  );

  loading$ = this.state$.pipe(map(state => state.loading));

  /**
   * Viewmodel that resolves once all the data is ready (or updated)...
   */
  vm$: Observable<ProductState> = combineLatest([
    // this.pagination$,
    this.criteria$,
    this.products$,
    this.loading$,
  ]).pipe(
    map(([criteria, products, loading]) => {
      return {
        criteria,
        products,
        loading,
      };
    }),
  );

  constructor(
    private httpApi: HttpApiService,
    private http: HttpClient,
    private userService: UserService,
    public subscriptionFacade: SubscriptionFacadeService,
  ) {
    combineLatest([this.criteria$, this.initCriteria()])
      .pipe(
        switchMap(([criteria, newCriteria]) => {
          return this.getProducts({ ...criteria, ...newCriteria });
        }),
      )
      .subscribe(products => {
        products.content;
        this.updateState({
          ..._state,
          products,

          loading: false,
        });
      });
  }
  initCriteria() {
    return this.userService.getUser().pipe(
      take(1),
      switchMap((user: UserAuthentified) => {
        const isConsultant = user?.sessionConsultant;

        return this.state$.pipe(
          take(1),
          map(state => {
            const pledge = this.subscriptionFacade.getPledge();
            const scpiOwnershipType = pledge.parameters.scpiOwnershipType;
            const isMain = pledge.owners.find(ow => ow.isMain);
            const physicalUser = isMain?.physicalUser;
            const newCriteria = {
              organizationIds: user?.organizationId
                ? [user?.organizationId]
                : [],
              disponibleInOrganization: true,
              isScpiAvailableInSimulationAndSubscription: true,
              realestateMappedSubscriptionForm: isConsultant
                ? null
                : scpiOwnershipType,
              electronicSignature: isConsultant ? null : true,
              subscriptionChildAllowed:
                isMain?.userAge < 18 || isMain?.child ? true : null,
              subscriptionMoralEntityAllowed:
                physicalUser == false ? true : null,
            };

            return newCriteria;
          }),
          distinctUntilChanged(),
        );
      }),
    );
  }
  getProducts(criteria?: ProductCriteria): Observable<ProductResponse> {
    return this.httpApi.post(
      API_ROUTE_PRODUCTS,
      criteria || { type: ['ASSURANCE_VIE'] },
    );
  }

  getStateSnapshot(): ProductState {
    return { ..._state, criteria: { ..._state.criteria } };
  }

  buildSearchTermControl(): UntypedFormControl {
    const searchTerm = new UntypedFormControl();
    searchTerm.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value: string) => this.updateSearchCriteria(value));

    return searchTerm;
  }

  updateSearchCriteria(criteria: string) {
    this.updateState({
      ..._state,
      criteria: { ..._state.criteria, autocomplete: criteria },
      loading: true,
    });
  }

  updatePagination(currentPage: number = 0) {
    const criteriaObj: ProductCriteria = {
      ..._state.criteria,
      page: currentPage + 1,
    };

    this.updateState({ ..._state, criteria: criteriaObj, loading: true });
  }

  private updateState(state: ProductState) {
    this.store.next((_state = state));
  }
}
